import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { useAuth } from "@/scripts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError } from "@/lib/toast";
import {
  recoverOrgSso,
  ssoErrorMessage,
  ssoReturnPath,
  startOrgSsoHop,
} from "@/lib/orgSso";

/**
 * Workplace SSO interstitial: hop at the IdP, or owner/admin recovery codes.
 */
const OrgSsoEnterPage = () => {
  const { orgId } = useParams();
  const { accessToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hopping, setHopping] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const hopError = searchParams.get("sso_error");

  const returnPath = useMemo(
    () => ssoReturnPath(orgId, searchParams.get("return")),
    [orgId, searchParams]
  );

  const load = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/status`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setStatus(res.data || {});
      if (res.data && !res.data.required) {
        window.location.replace(returnPath);
      }
    } catch (err) {
      console.error(err);
      toastError(err, "Could not check SSO status");
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken, returnPath]);

  useEffect(() => {
    load();
  }, [load]);

  const handleHop = async () => {
    setHopping(true);
    try {
      await startOrgSsoHop(orgId, {
        mode: "workplace",
        returnPath,
      });
    } catch (err) {
      toastError(err, "Could not start SSO");
      setHopping(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    setRecovering(true);
    try {
      await recoverOrgSso(orgId, { password, code });
      window.location.replace(returnPath);
    } catch (err) {
      const codeName =
        err?.response?.data?.code || err?.response?.data?.message?.code;
      if (codeName === "sso_recovery_exhausted") {
        toastError(ssoErrorMessage(codeName));
      } else {
        toastError(err, "Invalid password or recovery code");
      }
      setRecovering(false);
    }
  };

  if (loading || status == null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-sm text-muted-foreground">Checking workplace SSO…</p>
      </div>
    );
  }

  if (!status.required) {
    return null;
  }

  return (
    <div className="mx-auto max-w-md space-y-6 py-10">
      <div>
        <h1 className="text-xl font-semibold">Workplace SSO required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Continue with your company identity provider to enter this workplace.
          Circa login is unchanged.
        </p>
      </div>

      {hopError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {ssoErrorMessage(hopError)}
        </p>
      ) : null}

      <Button
        type="button"
        className="w-full"
        disabled={hopping}
        onClick={handleHop}
      >
        {hopping ? "Redirecting…" : "Continue with company SSO"}
      </Button>

      {hopError === "sso_invite_sub_mismatch" ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={hopping}
          onClick={async () => {
            setHopping(true);
            try {
              await startOrgSsoHop(orgId, {
                mode: "workplace",
                returnPath,
                prompt: "login",
              });
            } catch (err) {
              toastError(err, "Could not start SSO");
              setHopping(false);
            }
          }}
        >
          Try again as a different identity
        </Button>
      ) : null}

      {status.can_recover ? (
        <div className="space-y-3 rounded-lg border border-border/80 p-4">
          {showRecover ? (
            <form className="space-y-3" onSubmit={handleRecover}>
              <p className="text-sm font-medium">Use a recovery code</p>
              <p className="text-xs text-muted-foreground">
                Enter your Circa password and one unused org recovery code.
                This opens a one-hour workplace session so you can disable SSO
                or mint a new set.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="sso-recover-password">Circa password</Label>
                <Input
                  id="sso-recover-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sso-recover-code">Recovery code</Label>
                <Input
                  id="sso-recover-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="XXXX-XXXX-XXXX"
                  autoComplete="off"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={recovering}>
                {recovering ? "Checking…" : "Enter with recovery code"}
              </Button>
            </form>
          ) : (
            <button
              type="button"
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
              onClick={() => setShowRecover(true)}
            >
              Use a recovery code
            </button>
          )}
        </div>
      ) : status.recovery_exhausted ? (
        <p className="text-sm text-muted-foreground">
          No recovery codes remain. Contact Circa support to disable workplace
          SSO.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          If the identity provider is down, ask an organization owner or admin.
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        <Link to="/dashboard" className="underline-offset-4 hover:underline">
          Back to dashboard
        </Link>
      </p>
    </div>
  );
};

export default OrgSsoEnterPage;
